import { useEffect, useState, useMemo, useCallback } from "react";
import { useQuestionApi } from "../../../hooks/useQuestionApi";
import { MemberDTO, QuestionDTO, CourseEvaluationAnswerDTO, CourseEvaluationSubmissionDTO, QuestionSetDTO } from "../../../services/apiClient";
import { CourseTypeConstants } from "../../../constants/CourseTypeConstants";
import { QuestionSetConstants } from "../../../constants/QuestionSetConstants";
import QuestionSection from "../../../components/steps/course-evaluation/QuestionSection";
import AdminLayout from "../../../components/layout/AdminLayout";
import { useParams, useNavigate } from "react-router-dom";
import { useCourseApi } from "../../../hooks/useCourseApi";
import { useCourseEvaluationApi } from "../../../hooks/useCourseEvaluationApi";
import EvaluationInstructions from "../../../components/steps/course-evaluation/Instructions";
import EvaluationResults from "../../../components/steps/course-evaluation/EvaluationResults";
import Otp from "../../../components/auth/Otp";
import { getMemberId, isImpersonating } from "../../../utils/tokenUtils";
import { showAlert } from "../../../utils/sweetAlertUtils";
import { useMemberApi } from "../../../hooks/useMemberApi";
import { useQuestionSetApi } from "../../../hooks/useQuestionSetApi";
import { getOrCreateBrowserTrackingId, getBrowserTrackingId } from "../../../utils/trackingUtils";

type SectionResult = {
    total: number;
    correct: number;
    answered: number;
    questions: QuestionDTO[];
};

type EvaluationSummary = {
    relevantSections: string[];
    userAnswersMap: Record<string, string>;
    totalQuestions: number;
    correctAnswers: number;
    percentage: number;
    correctQuestionIds: Set<string>;
    sectionResults: {
        sectionA: SectionResult;
        sectionB1: SectionResult;
        sectionB2: SectionResult;
        sectionC: SectionResult;
        sectionD: SectionResult;
    };
};

const Evaluation: React.FC = () => {

    const navigate = useNavigate();

    const breadcrumbs = [
        { label: 'Admin', href: '#' },
        { label: 'Courses', href: '#' },
    ];

    const { id } = useParams<{ id: string }>();
    const memberId = getMemberId();
    const isAdminImpersonating = isImpersonating();

    const { getById: getByCourseId } = useCourseApi();
    const { submit: submitCourseEvaluation, checkExamineeId } = useCourseEvaluationApi();
    const [courseTypeId, setCourseTypeId] = useState<string | null>(null);
    useEffect(() => {
        const fetchEntityData = async () => {
            try {
                const response = await getByCourseId(id!);
                setCourseTypeId(response?.data?.courseTypeId || null);
            } catch (error) {
                console.error('Error fetching Course data:', error);
            }
        };

        fetchEntityData();
    }, [id]);

    const { search, isLoading, error } = useQuestionApi();
    const { search: searchQuestionSets } = useQuestionSetApi();
    const [questions, setQuestions] = useState<QuestionDTO[]>([]);
    const [totalQuestions, setTotalQuestions] = useState<number>(0);
    const [questionSetQuestionLimits, setQuestionSetQuestionLimits] = useState<Record<string, number>>({});
    useEffect(() => {
        const fetchItems = async (page: number, size: number, filters: any) => {
            try {
                const response = await search({
                    filters,
                    page,
                    pageSize: size,
                    sortBy: 'DisplayPosition',
                    sortDescending: false,
                });
                setQuestions(response?.data?.results || []);
                setTotalQuestions(response?.data?.totalCount || 0);
            } catch (error) {
                console.error('Error fetching Question Sets:', error);
            }
        };
        fetchItems(1, 9999, {});
    }, []);

    useEffect(() => {
        const fetchQuestionSetLimits = async () => {
            try {
                const response = await searchQuestionSets({
                    filters: {},
                    page: 1,
                    pageSize: 9999,
                    sortBy: 'DisplayPosition',
                    sortDescending: false,
                });

                const results = response?.data?.results || [];
                const limits: Record<string, number> = {};

                results.forEach((questionSet: QuestionSetDTO) => {
                    if (questionSet.id && typeof questionSet.questionsToShow === 'number') {
                        limits[questionSet.id.toUpperCase()] = questionSet.questionsToShow;
                    }
                });

                setQuestionSetQuestionLimits(limits);
            } catch (error) {
                console.error('Error fetching Question Set limits:', error);
            }
        };

        fetchQuestionSetLimits();
    }, []);


    const { getById: getMemberById, edit } = useMemberApi();
    const [member, setMember] = useState<MemberDTO | undefined>({} as MemberDTO);
    useEffect(() => {
        const fetchMember = async () => {
            try {
                const response = await getMemberById(memberId || '');
                const fetchedMember: MemberDTO | undefined = response?.data;
                setMember(fetchedMember);
            } catch (error) {
                console.error('Error fetching User', error);
            }
        };

        fetchMember();
    }, [memberId]);

    // Conditional steps based on course type
    const getStepsForCourseType = (courseTypeId: string) => {
        switch (courseTypeId) {
            case CourseTypeConstants.DEDICATED_HUNTER:
                // Hunter exam: Section A, B1, B2, D (no Section C)
                return ['Instructions', 'OTP', 'Section A', 'Section B1', 'Section B2', 'Section D', 'Finish'];
            case CourseTypeConstants.DEDICATED_SPORTS_PERSON:
                // Sports Person exam: Section A, B1, B2, C (no Section D)
                return ['Instructions', 'OTP', 'Section A', 'Section B1', 'Section B2', 'Section C', 'Finish'];
            case CourseTypeConstants.DEDICATED_HUNTER_AND_SPORTS_PERSON:
            default:
                // Both or default: All sections
                return ['Instructions', 'OTP', 'Section A', 'Section B1', 'Section B2', 'Section C', 'Section D', 'Finish'];
        }
    };

    const [steps, setSteps] = useState(getStepsForCourseType(courseTypeId!));
    const [currentStep, setCurrentStep] = useState(1);
    const currentStepName = steps[currentStep - 1] || '';
    const isQuestionStep = currentStepName.startsWith('Section ');
    const getRelevantSections = (currentCourseTypeId: string): string[] => {
        switch (currentCourseTypeId) {
            case CourseTypeConstants.DEDICATED_HUNTER:
                return [QuestionSetConstants.SECTION_A, QuestionSetConstants.SECTION_B1, QuestionSetConstants.SECTION_B2, QuestionSetConstants.SECTION_D];
            case CourseTypeConstants.DEDICATED_SPORTS_PERSON:
                return [QuestionSetConstants.SECTION_A, QuestionSetConstants.SECTION_B1, QuestionSetConstants.SECTION_B2, QuestionSetConstants.SECTION_C];
            case CourseTypeConstants.DEDICATED_HUNTER_AND_SPORTS_PERSON:
            default:
                return [QuestionSetConstants.SECTION_A, QuestionSetConstants.SECTION_B1, QuestionSetConstants.SECTION_B2, QuestionSetConstants.SECTION_C, QuestionSetConstants.SECTION_D];
        }
    };

    // Update steps when courseTypeId changes
    useEffect(() => {
        if (courseTypeId) {
            setSteps(getStepsForCourseType(courseTypeId));
        }
    }, [courseTypeId]);

    // State to track all evaluation answers
    const [evaluationAnswers, setEvaluationAnswers] = useState<CourseEvaluationAnswerDTO[]>([]);
    const [hasAutoSubmitted, setHasAutoSubmitted] = useState(false);

    // Timer state - 60 minutes (3600 seconds)
    const [timeRemaining, setTimeRemaining] = useState(3600);
    const [isTimerStarted, setIsTimerStarted] = useState(false);

    // Track evaluation start time
    const [evaluationStartTime, setEvaluationStartTime] = useState<Date | null>(null);

    // Memoize the format time function to prevent unnecessary re-calculations
    const formatTime = useCallback((seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;

        if (hours > 0) {
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
        }
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }, []);

    const applySectionQuestionLimit = useCallback((sectionId: string, sectionQuestions: QuestionDTO[]) => {
        const questionLimit = questionSetQuestionLimits[sectionId.toUpperCase()];

        if (typeof questionLimit !== 'number') {
            return sectionQuestions;
        }

        if (questionLimit <= 0) {
            return [];
        }

        return sectionQuestions.slice(0, questionLimit);
    }, [questionSetQuestionLimits]);

    // Memoize filtered questions to prevent re-filtering on every render
    const filteredQuestions = useMemo(() => ({
        sectionA: applySectionQuestionLimit(
            QuestionSetConstants.SECTION_A,
            questions.filter(question => question.questionSetId?.toUpperCase() === QuestionSetConstants.SECTION_A)
        ),
        sectionB1: applySectionQuestionLimit(
            QuestionSetConstants.SECTION_B1,
            questions.filter(question => question.questionSetId?.toUpperCase() === QuestionSetConstants.SECTION_B1)
        ),
        sectionB2: applySectionQuestionLimit(
            QuestionSetConstants.SECTION_B2,
            questions.filter(question => question.questionSetId?.toUpperCase() === QuestionSetConstants.SECTION_B2)
        ),
        sectionC: applySectionQuestionLimit(
            QuestionSetConstants.SECTION_C,
            questions.filter(question => question.questionSetId?.toUpperCase() === QuestionSetConstants.SECTION_C)
        ),
        sectionD: applySectionQuestionLimit(
            QuestionSetConstants.SECTION_D,
            questions.filter(question => question.questionSetId?.toUpperCase() === QuestionSetConstants.SECTION_D)
        ),
    }), [applySectionQuestionLimit, questions]);

    // Handle answer selection
    const handleAnswerChange = useCallback((questionId: string, userAnswerId: string) => {
        setEvaluationAnswers(prev => {
            // Remove any existing answer for this question
            const filtered = prev.filter(answer => answer.questionId !== questionId);

            // Add the new answer
            const newAnswer: CourseEvaluationAnswerDTO = {
                questionId,
                userAnswerId,
                memberId: memberId || undefined,
                // courseEvaluationId will be set when we create the evaluation session
                // isCorrect will be calculated server-side
            };

            return [...filtered, newAnswer];
        });
    }, [memberId]);

    // Get current answers in a format that QuestionSection can use
    const getCurrentAnswers = useCallback(() => {
        const answerMap: Record<string, string> = {};
        evaluationAnswers.forEach(answer => {
            if (answer.questionId && answer.userAnswerId) {
                answerMap[answer.questionId] = answer.userAnswerId;
            }
        });
        return answerMap;
    }, [evaluationAnswers]);

    const evaluationSummary = useMemo<EvaluationSummary>(() => {
        const relevantSections = getRelevantSections(courseTypeId || '');
        const questionsBySectionId: Record<string, QuestionDTO[]> = {
            [QuestionSetConstants.SECTION_A]: filteredQuestions.sectionA,
            [QuestionSetConstants.SECTION_B1]: filteredQuestions.sectionB1,
            [QuestionSetConstants.SECTION_B2]: filteredQuestions.sectionB2,
            [QuestionSetConstants.SECTION_C]: filteredQuestions.sectionC,
            [QuestionSetConstants.SECTION_D]: filteredQuestions.sectionD,
        };

        const filteredQuestionsForCourse = relevantSections.flatMap(sectionId => questionsBySectionId[sectionId] || []);

        const userAnswersMap: Record<string, string> = {};
        evaluationAnswers.forEach(answer => {
            if (answer.questionId && answer.userAnswerId) {
                userAnswersMap[answer.questionId] = answer.userAnswerId;
            }
        });

        let correctAnswers = 0;
        const correctQuestionIds = new Set<string>();

        const sectionResults = {
            sectionA: { total: 0, correct: 0, answered: 0, questions: [] as QuestionDTO[] },
            sectionB1: { total: 0, correct: 0, answered: 0, questions: [] as QuestionDTO[] },
            sectionB2: { total: 0, correct: 0, answered: 0, questions: [] as QuestionDTO[] },
            sectionC: { total: 0, correct: 0, answered: 0, questions: [] as QuestionDTO[] },
            sectionD: { total: 0, correct: 0, answered: 0, questions: [] as QuestionDTO[] },
        };

        filteredQuestionsForCourse.forEach(question => {
            if (!question.id) return;

            const userAnswerId = userAnswersMap[question.id];
            const correctOption = question.questionOptions?.find(option => option.isCorrectOption);
            const isCorrect = userAnswerId && correctOption?.id === userAnswerId;
            const isAnswered = !!userAnswerId;
            const isAnsweredIncorrectly = userAnswerId && !isCorrect;

            if (isCorrect) {
                correctAnswers++;
                correctQuestionIds.add(question.id);
            }

            const sectionId = question.questionSetId?.toUpperCase();
            switch (sectionId) {
                case QuestionSetConstants.SECTION_A:
                    sectionResults.sectionA.total++;
                    if (isAnswered) sectionResults.sectionA.answered++;
                    if (isCorrect) sectionResults.sectionA.correct++;
                    if (isAnsweredIncorrectly) sectionResults.sectionA.questions.push(question);
                    break;
                case QuestionSetConstants.SECTION_B1:
                    sectionResults.sectionB1.total++;
                    if (isAnswered) sectionResults.sectionB1.answered++;
                    if (isCorrect) sectionResults.sectionB1.correct++;
                    if (isAnsweredIncorrectly) sectionResults.sectionB1.questions.push(question);
                    break;
                case QuestionSetConstants.SECTION_B2:
                    sectionResults.sectionB2.total++;
                    if (isAnswered) sectionResults.sectionB2.answered++;
                    if (isCorrect) sectionResults.sectionB2.correct++;
                    if (isAnsweredIncorrectly) sectionResults.sectionB2.questions.push(question);
                    break;
                case QuestionSetConstants.SECTION_C:
                    sectionResults.sectionC.total++;
                    if (isAnswered) sectionResults.sectionC.answered++;
                    if (isCorrect) sectionResults.sectionC.correct++;
                    if (isAnsweredIncorrectly) sectionResults.sectionC.questions.push(question);
                    break;
                case QuestionSetConstants.SECTION_D:
                    sectionResults.sectionD.total++;
                    if (isAnswered) sectionResults.sectionD.answered++;
                    if (isCorrect) sectionResults.sectionD.correct++;
                    if (isAnsweredIncorrectly) sectionResults.sectionD.questions.push(question);
                    break;
            }
        });

        const totalQuestions = filteredQuestionsForCourse.length;
        const percentage = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;

        return {
            relevantSections,
            userAnswersMap,
            totalQuestions,
            correctAnswers,
            percentage,
            correctQuestionIds,
            sectionResults,
        };
    }, [courseTypeId, evaluationAnswers, filteredQuestions]);

    const submitEvaluation = useCallback(async () => {
        try {
            const scorePercentage = evaluationSummary.percentage;

            // Single atomic call: the backend creates the CourseEvaluation record,
            // saves all answers, determines pass/fail from the score, and updates
            // the Course's evaluation/course status - all in one transaction.
            const submission: CourseEvaluationSubmissionDTO = {
                courseId: id!,
                memberId: memberId!,
                courseTypeId: courseTypeId!,
                startTime: evaluationStartTime!,
                endTime: new Date(),
                scorePercentage: scorePercentage,
                scoreMark: evaluationSummary.correctAnswers,
                cookieGuid: getOrCreateBrowserTrackingId(),
                answers: evaluationAnswers.map(answer => ({
                    questionId: answer.questionId,
                    userAnswerId: answer.userAnswerId,
                    isCorrect: answer.questionId ? evaluationSummary.correctQuestionIds.has(answer.questionId) : false,
                })),
            };

            const response = await submitCourseEvaluation(submission);

            if (response && response.success && response.data) {
                console.log('Evaluation results saved successfully:', {
                    score: scorePercentage,
                    status: response.data.isPassed ? 'PASSED' : 'FAILED'
                });
                return;
            }

            setHasAutoSubmitted(false);
            alert('Failed to save evaluation results. Please try again.');
        } catch (error) {
            console.error('Error saving evaluation results:', error);
            setHasAutoSubmitted(false);
            alert('Failed to save evaluation results. Please try again.');
        }
    }, [
        courseTypeId,
        submitCourseEvaluation,
        evaluationAnswers,
        evaluationSummary,
        evaluationStartTime,
        id,
        memberId,
    ]);

    useEffect(() => {
        if (steps[currentStep - 1] === 'Finish' && !hasAutoSubmitted) {
            setHasAutoSubmitted(true);
            void submitEvaluation();
        }
    }, [currentStep, hasAutoSubmitted, steps, submitEvaluation]);

    // Timer effect - separate from other effects to prevent interference
    useEffect(() => {
        if (!isTimerStarted || !isQuestionStep) return;

        // Set the evaluation start time when the timer begins
        if (!evaluationStartTime) {
            const attemptTime = new Date();
            setEvaluationStartTime(attemptTime);

            // Detect shared-PC usage: if a tracking ID already exists in local storage,
            // ask the backend whether it was previously used by a different member.
            // The backend owns the flagging decision and note creation - the frontend
            // just fires the check and forgets about it.
            const existingTrackingId = getBrowserTrackingId();
            if (existingTrackingId && memberId && id) {
                void checkExamineeId({
                    examineeId: existingTrackingId,
                    memberId,
                    courseId: id
                });
            }

            // Generate browser tracking ID when evaluation starts (for anti-cheating detection)
            getOrCreateBrowserTrackingId();
        }

        const timer = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev <= 1) {
                    // Handle time up - navigate to Finish step
                    clearInterval(timer);

                    void (async () => {
                        // Show alert
                        await showAlert({
                            title: 'Time\'s Up!',
                            text: 'Your time has expired. Your completed answers will be submitted.',
                            icon: 'warning',
                            confirmButtonText: 'View Results'
                        });

                        // Navigate to the Finish step - let existing logic handle submission
                        setCurrentStep(steps.length);
                    })();

                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [isTimerStarted, isQuestionStep, evaluationStartTime, steps]);

    const renderStepContent = () => {
        switch (currentStepName) {

            case 'Instructions':
                return (
                    <EvaluationInstructions
                        member={member}
                        onNext={() => setCurrentStep(currentStep + 1)}
                        onPrevious={() => navigate('/admin/dedicatedstatus')} />
                );
            case 'OTP':
                return (
                    <Otp
                        member={member}
                        onNext={() => {
                            setIsTimerStarted(true);
                            setCurrentStep(currentStep + 1);
                        }}
                    />
                );
            case 'Section A':
                return (
                    <QuestionSection
                        title="Section A"
                        currentStep={currentStep}
                        data={filteredQuestions.sectionA}
                        selectedAnswers={getCurrentAnswers()}
                        onAnswerChange={handleAnswerChange}
                        onNext={() => setCurrentStep(currentStep + 1)}
                        onPrevious={() => setCurrentStep(Math.max(1, currentStep - 1))}
                        showCorrectAnswers={isAdminImpersonating} />
                );
            case 'Section B1':
                return (
                    <QuestionSection
                        title="Section B1"
                        currentStep={currentStep}
                        data={filteredQuestions.sectionB1}
                        selectedAnswers={getCurrentAnswers()}
                        onAnswerChange={handleAnswerChange}
                        onNext={() => setCurrentStep(currentStep + 1)}
                        onPrevious={() => setCurrentStep(Math.max(1, currentStep - 1))}
                        showCorrectAnswers={isAdminImpersonating} />
                );
            case 'Section B2':
                return (
                    <QuestionSection
                        title="Section B2"
                        currentStep={currentStep}
                        data={filteredQuestions.sectionB2}
                        selectedAnswers={getCurrentAnswers()}
                        onAnswerChange={handleAnswerChange}
                        onNext={() => setCurrentStep(currentStep + 1)}
                        onPrevious={() => setCurrentStep(Math.max(1, currentStep - 1))}
                        showCorrectAnswers={isAdminImpersonating} />
                );
            case 'Section C':
                return (
                    <QuestionSection
                        title="Section C"
                        currentStep={currentStep}
                        data={filteredQuestions.sectionC}
                        selectedAnswers={getCurrentAnswers()}
                        onAnswerChange={handleAnswerChange}
                        onNext={() => setCurrentStep(currentStep + 1)}
                        onPrevious={() => setCurrentStep(Math.max(1, currentStep - 1))}
                        showCorrectAnswers={isAdminImpersonating} />
                );
            case 'Section D':
                return (
                    <QuestionSection
                        title="Section D"
                        currentStep={currentStep}
                        data={filteredQuestions.sectionD}
                        selectedAnswers={getCurrentAnswers()}
                        onAnswerChange={handleAnswerChange}
                        onNext={() => setCurrentStep(currentStep + 1)}
                        onPrevious={() => setCurrentStep(Math.max(1, currentStep - 1))}
                        showCorrectAnswers={isAdminImpersonating} />
                );
            case 'Finish':
                return (
                    <EvaluationResults
                        evaluationAnswers={evaluationAnswers}
                        relevantSections={evaluationSummary.relevantSections}
                        userAnswersMap={evaluationSummary.userAnswersMap}
                        results={{
                            totalQuestions: evaluationSummary.totalQuestions,
                            correctAnswers: evaluationSummary.correctAnswers,
                            percentage: evaluationSummary.percentage,
                            sectionResults: evaluationSummary.sectionResults,
                        }}
                        {...(evaluationSummary.percentage < 70 && {
                            onRetry: () => {
                                setEvaluationAnswers([]);
                                setHasAutoSubmitted(false);
                                setIsTimerStarted(false);
                                setTimeRemaining(3600);
                                setEvaluationStartTime(null);
                                setCurrentStep(1);
                            }
                        })}
                    />
                );
            default:
                return null;
        }
    };

    return (

        <AdminLayout
            activeMenuItem="/admin/dedicatedstatus"
            breadcrumbs={breadcrumbs}
            allowedRoles={['Admin', 'User']}
            requiredPermission="Manage.Evaluation.Edit"
        >
            <div className="relative">

                {isTimerStarted && isQuestionStep && (
                    <div className="fixed top-40 right-8 z-[9999]">
                        <div className={`px-4 py-2 rounded-lg shadow-lg font-mono text-lg font-bold ${timeRemaining <= 300 ? 'bg-red-500 text-white' :
                            timeRemaining <= 600 ? 'bg-orange-500 text-black' :
                                'bg-primary text-white'
                            }`}>
                            <div className="text-sm">Time Remaining</div>
                            <div className="text-xl">{formatTime(timeRemaining)}</div>
                        </div>
                    </div>
                )}

                {questions && questions.length !== 0 && (
                    <div className="mt-5 sm:mt-8">{renderStepContent()}</div>
                )}
            </div>

        </AdminLayout>
    );
}

export default Evaluation;



